"use client";

import { useRef, useState } from "react";
import Text from "@/components/static/Text";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import InvalidText from "@/components/ui/InvalidText";
import { cn } from "@/lib/utils";
import Button from "@/components/static/Button";
import { Spin } from "@/icons";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { TermProps, JobProps } from "@/lib/types";
import Link from "next/link";
import { Label } from "@/components/ui/Label";
import parse from "html-react-parser";

export default function ApplicationForm({
    jobTypes,
    openJobs,
    setJobtype,
    setJobname,
    bg,
    vat,
    translations,
    locale,
    textnotice,
}: {
    jobTypes: TermProps[];
    openJobs: JobProps[];
    setJobtype?: string;
    setJobname?: string;
    bg?: string;
    vat?: string;
    translations?: any;
    locale?: string;
    textnotice?: string;
}) {
    const formRef = useRef<HTMLFormElement | null>(null);
    const [error, setError] = useState<string | boolean>(false);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm();

    const methods = useForm();

    const lang = locale || "en";

    async function submitForm(data: any) {
        setLoading(true);
        setError(false);
        setSuccess(false);
        if (!formRef.current) return;

        const formData = new FormData(formRef.current);

        try {
            const res = await fetch("/api/mail/job", {
                method: "POST",
                body: formData,
            });

            const data = (await res.json()) as { status: boolean };

            if (data.status) {
                setError(false);
                setSuccess(true);
                reset();
            } else {
                setError(true);
                setSuccess(false);
            }
        } catch (e) {
            setError(true);
            setSuccess(false);
        } finally {
            setLoading(false);
        }
    }

    const PrivacyPolicyAcceptance = () => {
        const textStart = translations.formPrivacyPolicyAcceptanceStart[lang];
        const textEnd = translations.formPrivacyPolicyAcceptanceEnd[lang];
        const label = translations.formPrivacyPolicyLabel[lang];
        const url = translations.formPrivacyPolicyURL[lang];

        return (
            <>
                {textStart}
                <Link className='prose-a:text-white underline' href={url}>
                    {label}
                </Link>
                {textEnd}
            </>
        );
    };

    return (
        <FormProvider {...methods}>
            <form
                ref={formRef}
                className={cn(
                    "scroll-m-60",
                    bg === "brand-light" ? "text-brand-dark" : "text-white"
                )}
                onSubmit={handleSubmit(submitForm)}
                noValidate
                id={vat != "" ? "anfrage" : ""}
            >
                <div className='max-w-xl'>
                    <div className='flex flex-col gap-6'>
                        <div className='text-h5'>{translations.formPersonalDataHeadline[lang]}</div>
                        <fieldset className='grid grid-cols-1 sm:grid-cols-2 sm:gap-6 gap-6'>
                            <FieldWrapper invalid={errors?.firstname}>
                                <Input
                                    bg={bg}
                                    id='firstname'
                                    itemID='firstname'
                                    type='text'
                                    placeholder={translations.formLabelFirstName[lang]}
                                    {...register("firstname", { required: true })}
                                />
                                <Label htmlFor='firstname'>
                                    {translations.formLabelFirstName[lang]}
                                </Label>
                                <InvalidText label={translations.errorFieldRequired[lang]} />
                            </FieldWrapper>
                            <FieldWrapper invalid={errors?.lastname}>
                                <Input
                                    bg={bg}
                                    type='text'
                                    placeholder={translations.formLabelLastName[lang]}
                                    {...register("lastname", { required: true })}
                                />
                                <Label htmlFor='lastname'>
                                    {translations.formLabelLastName[lang]}
                                </Label>
                                <InvalidText label={translations.errorFieldRequired[lang]} />
                            </FieldWrapper>
                        </fieldset>
                        <fieldset className='grid grid-cols-1 sm:grid-cols-2 sm:gap-6 gap-4'>
                            <FieldWrapper invalid={errors?.email}>
                                <Input
                                    bg={bg}
                                    type='email'
                                    placeholder={translations.formLabelEmail[lang]}
                                    {...register("email", { required: true })}
                                />
                                <Label htmlFor='email'>{translations.formLabelEmail[lang]}</Label>
                                <InvalidText label={translations.errorFieldRequired[lang]} />
                            </FieldWrapper>
                            <FieldWrapper invalid={errors?.phone}>
                                <Input
                                    bg={bg}
                                    type='tel'
                                    placeholder={"+49 …"}
                                    {...register("phone", { required: true })}
                                />
                                <Label htmlFor='phone'>{translations.formLabelPhone[lang]}</Label>
                                <InvalidText label={translations.errorFieldRequired[lang]} />
                            </FieldWrapper>
                        </fieldset>
                        <div className='text-h5'>{translations.formSubjectHeadline[lang]}</div>
                        <fieldset className='grid grid-cols-1 sm:grid-cols-2 sm:gap-6 gap-4'>
                            <FieldWrapper invalid={errors?.subject}>
                                <Input
                                    bg={bg}
                                    type='text'
                                    placeholder={translations.formLabelSubject[lang]}
                                    {...register("subject", { required: true })}
                                />
                                <Label htmlFor='address'>
                                    {translations.formLabelSubject[lang]}
                                </Label>
                                <InvalidText label={translations.errorFieldRequired[lang]} />
                            </FieldWrapper>
                            {vat && (
                                <FieldWrapper invalid={errors?.sku}>
                                    <Input
                                        bg={bg}
                                        type='text'
                                        placeholder={translations.formLabelSKU[lang]}
                                        {...register("sku", { required: true, value: vat })}
                                    />
                                    <Label htmlFor='postal'>
                                        {translations.formLabelSKU[lang]}
                                    </Label>
                                    <InvalidText label={translations.errorFieldRequired[lang]} />
                                </FieldWrapper>
                            )}
                        </fieldset>
                        <div className='text-h5'>{translations.formMessageHeadline[lang]}</div>
                        <FieldWrapper invalid={errors?.message}>
                            <Textarea
                                className={cn(
                                    "placeholder:text-white focus:placeholder-brand-dark text-white border-stroke focus:bg-white focus:text-brand-dark",
                                    bg === "brand-light" &&
                                        "placeholder:text-brand-dark border-brand-dark focus:placeholder:text-white focus:bg-brand-dark"
                                )}
                                {...register("message", { required: false })}
                                placeholder={translations.formLabelMessage[lang]}
                            />
                            <Label htmlFor='message'>{translations.formLabelMessage[lang]}</Label>
                        </FieldWrapper>
                        <FieldWrapper invalid={errors?.privacy}>
                            <div className='flex gap-2'>
                                <input
                                    type='checkbox'
                                    id='privacy'
                                    {...register("privacy", { required: true })}
                                />
                                <label htmlFor='privacy'>
                                    <div
                                        className={cn(
                                            "text-sm",
                                            bg === "brand-light"
                                                ? "text-brand-dark prose-a:text-brand-dark"
                                                : "text-white"
                                        )}
                                    >
                                        <PrivacyPolicyAcceptance />
                                    </div>
                                </label>
                            </div>
                            <InvalidText
                                label={translations.errorPrivacyAcceptanceRequired[lang]}
                            />
                        </FieldWrapper>
                        <div>
                            <Button
                                as='button'
                                type={bg === "brand-light" ? "primary" : "primary"}
                                disabled={loading}
                            >
                                <span>
                                    {!loading
                                        ? translations.sendFormLabel[lang]
                                        : translations.sendIsSending[lang]}
                                </span>
                                {loading && <Spin className='ms-4 size-4 animate-spin' />}
                            </Button>

                            {error && (
                                <div className='mt-6 text-black rounded-sm border bg-error px-4 py-3 [&_a]:font-bold [&_a]:hover:underline'>
                                    {typeof error === "string"
                                        ? error
                                        : translations.errorFormSubmit[lang]}
                                </div>
                            )}
                            {success && (
                                <div className='mt-6 rounded-full bg-success text-brand-dark px-4 py-3 text-center'>
                                    {translations.successFormSubmit[lang]}
                                </div>
                            )}
                        </div>
                        <Text
                            className={cn(
                                "text-white text-sm prose-strong:text-white prose-a:text-white prose-a:underline",
                                bg === "brand-light" &&
                                    "text-brand-dark prose-strong:text-brand-dark prose-a:text-brand-dark"
                            )}
                        >
                            {parse(textnotice ?? "")}
                        </Text>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

const FieldWrapper = ({
    invalid,
    className,
    children,
    ...rest
}: {
    invalid?: any;
    className?: string;
    children?: any;
}) => {
    return (
        <div className={cn("flex flex-col relative", invalid && "invalid", className)} {...rest}>
            {children}
        </div>
    );
};
